// Failure events Denver reports, shared by the page and the desktop app's main process
// (CommonJS so both can load it). Mirrors Tokyo's registry in cloud/services/client_events.py,
// which re-validates everything: only registered names and fields, each value of its type.
// Fields are dropped by name, never echoed by value.

const MAX_ID = Number.MAX_SAFE_INTEGER;
const MAX_INT = 2 ** 31 - 1;
const MAX_MS = 86400000;
const CODE = /^[a-z0-9][a-z0-9_.:-]{0,63}$/;
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const PATH = /^\/[A-Za-z0-9_./{}-]{0,200}$/;
// API route words stay; every other segment (ids, UUIDs, anything that could be a name) is {id}.
const ROUTE_WORD = /^[a-z][a-z_-]*$/;

const integer = (low, high) => value => (Number.isInteger(value) && value >= low && value <= high ? value : null);
const matching = (pattern, transform = value => value) => value =>
  (typeof value === 'string' && pattern.test(value) ? transform(value) : null);
const oneOf = (...allowed) => value => (typeof value === 'string' && allowed.includes(value) ? value : null);

const routeTemplate = value => {
  if (typeof value !== 'string') return null;
  const path = value.split(/[?#]/)[0]
    .split('/')
    .map(segment => (!segment || ROUTE_WORD.test(segment) ? segment : '{id}'))
    .join('/');
  return PATH.test(path) ? path : null;
};

const TYPES = {
  id: integer(1, MAX_ID),
  int: integer(0, MAX_INT),
  ms: integer(0, MAX_MS),
  status: integer(100, 599),
  code: matching(CODE),
  ref: matching(UUID, value => value.toLowerCase()),
  path: routeTemplate,
  connection_type: oneOf('webconnect', 'rdp', 'ssh_tunnel', 'udp_relay', 'freerdp'),
  logout_reason: oneOf('expired', 'forbidden', 'network', 'timeout'),
  proxy_target: oneOf('tokyo', 'kyoto', 'webconnect'),
};

const event = (fields, { anonymous = false } = {}) => ({ anonymous, fields });

const REGISTRY = {
  'auth.oauth.state_mismatch': event({}, { anonymous: true }),
  'auth.token_exchange.failed': event({ status: 'status', error_code: 'code' }, { anonymous: true }),
  'auth.session.forced_logout': event({
    reason: 'logout_reason', path: 'path', status: 'status', minutes_since_login: 'int', request_id: 'ref',
  }),
  'app.boot.failed': event({ stage: 'code', error_kind: 'code' }, { anonymous: true }),
  'app.error.unhandled': event({ error_kind: 'code', source: 'code' }),
  'connection.launch.failed': event({
    connection_type: 'connection_type',
    step: 'code',
    vm_id: 'id',
    perm_id: 'id',
    error_code: 'code',
    status: 'status',
    request_id: 'ref',
    operation_id: 'ref',
  }),
  'connection.session.disconnected': event({
    connection_type: 'connection_type', reason: 'code', guac_status: 'int', duration_ms: 'ms', vm_id: 'id', perm_id: 'id',
  }),
  'electron.proxy.upstream_failed': event({ target: 'proxy_target', error_code: 'code' }),
  'electron.window.failed': event({ reason: 'code', exit_code: 'int' }),
  'update.failed': event({ stage: 'code', error_code: 'code' }),
  'update.skipped': event({ reason: 'code' }),
  'copilot.turn.failed': event({
    copilot_session: 'id', turn_request_id: 'ref', status: 'status', error_kind: 'code', attempt: 'int',
  }),
  'copilot.results.dropped': event({ copilot_session: 'id', results: 'int' }),
  'copilot.compile.failed': event({ stage: 'code', status: 'status', error_kind: 'code' }),
  'copilot.recording.failed': event({ step: 'code', error_kind: 'code' }),
};

// The event to record, and the names of the fields that were dropped. An unregistered name
// builds nothing.
const MAX_FIELDS = 32;

const buildEvent = (name, fields = {}, now = new Date()) => {
  const spec = typeof name === 'string' && Object.prototype.hasOwnProperty.call(REGISTRY, name) ? REGISTRY[name] : null;
  if (!spec || Object.keys(fields || {}).length > MAX_FIELDS) return { event: null, dropped: [] };

  const kept = {};
  const dropped = [];
  for (const [field, value] of Object.entries(fields || {})) {
    const type = Object.prototype.hasOwnProperty.call(spec.fields, field) ? spec.fields[field] : null;
    const checked = type ? TYPES[type](value) : null;
    if (checked === null || checked === undefined) {
      dropped.push(field);
    } else {
      kept[field] = checked;
    }
  }
  return { event: { name, occurred_at: now.toISOString(), fields: kept }, dropped };
};

// JavaScript's and the DOM's error names. A thrown value's name is free text (anything can set
// it), so only these are reported; the rest are "other".
const ERROR_NAMES = [
  'Error', 'TypeError', 'RangeError', 'ReferenceError', 'SyntaxError', 'EvalError', 'URIError', 'AggregateError',
  'AbortError', 'TimeoutError', 'NetworkError', 'NotAllowedError', 'NotFoundError', 'NotReadableError',
  'NotSupportedError', 'InvalidStateError', 'InvalidAccessError', 'SecurityError', 'QuotaExceededError',
  'DataError', 'DataCloneError', 'EncodingError', 'OperationError', 'OverconstrainedError', 'ConstraintError',
  'TransactionInactiveError', 'ReadOnlyError', 'VersionError', 'UnknownError', 'AxiosError', 'ChunkLoadError',
];
const ERROR_KINDS = new Map(ERROR_NAMES.map(name => [name, name.toLowerCase()]));

// A short lower-case code for any thrown value ("TypeError" -> "typeerror").
const errorKind = error => {
  if (!error || typeof error !== 'object' || typeof error.name !== 'string') return 'non_error';
  return ERROR_KINDS.get(error.name) || 'other';
};

// OAuth 2.0 (RFC 6749) and OpenID Connect error codes. An OAuth error arrives in a URL or a
// response body, so only these are reported; anything else is "other".
const OAUTH_ERRORS = new Set([
  'invalid_request', 'unauthorized_client', 'access_denied', 'unsupported_response_type', 'invalid_scope',
  'server_error', 'temporarily_unavailable', 'invalid_client', 'invalid_grant', 'unsupported_grant_type',
  'interaction_required', 'login_required', 'account_selection_required', 'consent_required',
  'invalid_request_uri', 'invalid_request_object', 'request_not_supported', 'request_uri_not_supported',
  'registration_not_supported',
]);
const oauthErrorCode = value => (typeof value === 'string' && OAUTH_ERRORS.has(value) ? value : 'other');

module.exports = { REGISTRY, buildEvent, errorKind, oauthErrorCode };
